import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gift, Printer } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";

const field = "w-full px-4 py-3 rounded-xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]";

const Option = ({ o, on, pick }) => (
  <button type="button" onClick={() => pick(o.id)} data-testid={`gift-option-${o.id}`}
    className={`text-left rounded-2xl border p-4 transition-colors ${on ? "border-[#2C4A3E] bg-[#2C4A3E] text-white" : "border-[#E6DEC3] bg-white hover:bg-[#F7F3EB]"}`}>
    <p className="text-sm font-medium">{o.label}</p>
    <p className="font-display text-2xl mt-1">${o.price.toFixed(0)}</p>
    <p className={`text-[11px] mt-1 ${on ? "text-white/70" : "text-stone-500"}`}>{o.plan === "Parent" ? "1 learner" : "Up to 4 learners"} · one-time, no auto-renew</p>
  </button>
);

export function GiftPage() {
  const [opts, setOpts] = useState([]);
  const [f, setF] = useState({ option: "parent_12m", buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/public/gifts/options").then((r) => setOpts(r.data.options)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { window.location.href = (await http.post("/public/gifts/checkout", { ...f, message: f.message || null, origin_url: window.location.origin })).data.checkout_url; }
    catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };
  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="gift-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Give the gift of mindful learning</span>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2">Gift ConsciousLearn</h1>
      <p className="text-stone-600 mt-3 max-w-2xl text-sm">Perfect for grandparents, aunts and uncles. Pick a gift, and we'll email the family a code to redeem. No account needed to buy.</p>
      <form onSubmit={submit} className="mt-8 space-y-8">
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">{opts.map((o) => <Option key={o.id} o={o} on={f.option === o.id} pick={(id) => setF({ ...f, option: id })} />)}</div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-3"><p className="text-sm font-medium text-[#2C4A3E]">From you</p>
            <input required maxLength={80} value={f.buyer_name} onChange={set("buyer_name")} placeholder="Your name" className={field} data-testid="gift-buyer-name" />
            <input required type="email" value={f.buyer_email} onChange={set("buyer_email")} placeholder="Your email (for the receipt)" className={field} data-testid="gift-buyer-email" /></div>
          <div className="space-y-3"><p className="text-sm font-medium text-[#2C4A3E]">To the family</p>
            <input required maxLength={80} value={f.recipient_name} onChange={set("recipient_name")} placeholder="Parent's name" className={field} data-testid="gift-recipient-name" />
            <input required type="email" value={f.recipient_email} onChange={set("recipient_email")} placeholder="Parent's email" className={field} data-testid="gift-recipient-email" /></div>
        </div>
        <textarea maxLength={300} rows={3} value={f.message} onChange={set("message")} placeholder="A short note (optional), e.g. Happy learning, Maya! Love, Grandma" className={field} data-testid="gift-message" />
        <button disabled={busy} data-testid="gift-checkout-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#C87D53] text-white text-sm disabled:opacity-50"><Gift className="w-4 h-4" />{busy ? "Opening checkout…" : "Continue to payment"}</button>
      </form>
    </div>
  );
}

export function GiftThanks() {
  const [q] = useSearchParams();
  const [g, setG] = useState(null);
  useEffect(() => {
    let n = 0;
    const poll = () => http.get(`/public/gifts/status/${q.get("session_id")}`).then((r) => { setG(r.data); if (r.data.status === "pending" && n++ < 10) setTimeout(poll, 2000); }).catch(() => setG({ status: "missing" }));
    poll();
  }, [q]);
  if (!g) return <p className="p-16 text-center text-stone-500">Loading your gift…</p>;
  if (g.status === "missing") return <p className="p-16 text-center text-stone-500" data-testid="gift-missing">We couldn't find that gift.</p>;
  if (g.status === "pending") return <p className="p-16 text-center text-stone-500" data-testid="gift-pending">Confirming your payment… this page will update in a moment.</p>;
  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="gift-thanks">
      <div className="no-print mb-6"><h1 className="font-display text-4xl text-[#2C4A3E]">Thank you!</h1>
        <p className="text-sm text-stone-600 mt-2">{g.recipient_emailed === false ? `We couldn't email ${g.recipient_name} right now, so please print or share this card.` : `We emailed the code to ${g.recipient_name} and sent you a receipt. You can also print this card.`}</p>
        <button onClick={() => window.print()} data-testid="gift-print-btn" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm"><Printer className="w-4 h-4" />Print gift card</button></div>
      <div className="rounded-[2rem] border-2 border-dashed border-[#C87D53] bg-[#FBF3E2] p-8 text-center" data-testid="gift-card">
        <Gift className="w-10 h-10 mx-auto text-[#C87D53]" />
        <p className="text-xs uppercase tracking-[0.3em] text-[#C87D53] mt-3">A gift for {g.recipient_name}</p>
        <p className="font-display text-3xl text-[#2C4A3E] mt-2">{g.label}</p>
        <p className="text-sm text-stone-600 mt-1">of ConsciousLearn, from {g.buyer_name}</p>
        {g.message && <p className="mt-4 italic text-stone-700">“{g.message}”</p>}
        <p className="mt-6 text-xs text-stone-500">Gift code</p>
        <p className="font-display text-3xl tracking-[0.2em] text-[#2C4A3E]" data-testid="gift-code">{g.code}</p>
        <p className="mt-4 text-xs text-stone-500">Redeem at <span className="break-all text-[#2C4A3E]" data-testid="gift-redeem-url">{g.redeem_url}</span></p>
      </div>
    </div>
  );
}

export const RedeemBox = ({ initial = "", onDone }) => {
  const [code, setCode] = useState(initial);
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = (await http.post("/gifts/redeem", { code })).data; toast.success(`Gift from ${r.from} redeemed! Active until ${new Date(r.until).toLocaleDateString()}`); setCode(""); setTimeout(() => window.location.reload(), 1200); onDone?.(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="flex gap-2" data-testid="redeem-form">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="XXXX-XXXX-XXXX" className={`${field} tracking-widest`} data-testid="redeem-code-input" />
      <button disabled={busy || code.length < 6} data-testid="redeem-submit-btn" className="px-5 rounded-full bg-[#C87D53] text-white text-sm disabled:opacity-50">Redeem</button>
    </form>
  );
};

export function RedeemPage() {
  const [q] = useSearchParams();
  const { profile } = useProfile();
  const code = q.get("code") || "";
  return (
    <div className="max-w-xl mx-auto px-6 py-14" data-testid="redeem-page">
      <h1 className="font-display text-4xl text-[#2C4A3E]">Redeem your gift</h1>
      {profile ? <div className="mt-6"><RedeemBox initial={code} onDone={() => setTimeout(() => (window.location.href = "/parent"), 1200)} /></div> : (
        <div className="mt-6 space-y-3 text-sm text-stone-600">
          <p>Your code: <b className="tracking-widest text-[#2C4A3E]" data-testid="redeem-page-code">{code}</b></p>
          <p>Create a free parent account (or log in), then open <b>Billing & plan</b> from your account menu and enter the code.</p>
          <div className="flex gap-3"><Link to="/register" data-testid="redeem-register" className="px-5 py-2.5 rounded-full bg-[#C87D53] text-white">Create account</Link>
            <Link to="/login" data-testid="redeem-login" className="px-5 py-2.5 rounded-full border border-[#E6DEC3]">Log in</Link></div>
        </div>
      )}
    </div>
  );
}
